import { useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Box,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Pagination,
  Paper,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ChatRoundedIcon from "@mui/icons-material/ChatRounded";
import CloseIcon from "@mui/icons-material/Close";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import SearchIcon from "@mui/icons-material/Search";
import SendIcon from "@mui/icons-material/Send";
import parse from "html-react-parser";
import MDButton from "components/MDButton";
import MDBox from "components/MDBox";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import {
  askAIChatCall,
  getAIMessagesByThreadCall,
  getAllAIThreadsCall,
} from "../../redux/api/ai.api";

function AIHistory() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedThread, setSelectedThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [chatOpen, setChatOpen] = useState(false);
  const [chatLoading, setChatLoading] = useState(false);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [page, setPage] = useState(1);
  const pageSize = 10;

  const renderAiHtml = (html) => (
    <Box
      sx={{
        fontSize: 15,
        lineHeight: 1.6,
        "& p": { margin: 0, mb: 1 },
        "& p:last-of-type": { mb: 0 },
        "& ul, & ol": { margin: "6px 0 0", pl: 2.5 },
        "& li": { marginBottom: 0.5 },
        "& li:last-of-type": { marginBottom: 0 },
        "& strong, & b": { fontWeight: 700 },
      }}
    >
      {parse(html || "")}
    </Box>
  );

  const fetchThreads = async () => {
    setLoading(true);
    try {
      const locale = (localStorage.getItem("i18nextLng") || "en").split("-")[0];
      const res = await getAllAIThreadsCall(locale);
      const grouped = res?.threads || {};
      const flat = Object.entries(grouped).flatMap(([groupLabel, items]) =>
        (Array.isArray(items) ? items : []).map((item) => ({
          ...item,
          groupLabel,
        }))
      );
      flat.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setThreads(flat);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchThreads();
  }, []);

  const filteredThreads = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return threads;
    return threads.filter((t) => (t.first_user_message || "").toLowerCase().includes(q));
  }, [threads, search]);

  const pageCount = Math.max(1, Math.ceil(filteredThreads.length / pageSize));
  const pagedThreads = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredThreads.slice(start, start + pageSize);
  }, [filteredThreads, page]);

  useEffect(() => {
    setPage(1);
  }, [search]);

  useEffect(() => {
    if (page > pageCount) {
      setPage(pageCount);
    }
  }, [page, pageCount]);

  const openThread = async (thread) => {
    setSelectedThread(thread);
    setChatOpen(true);
    setChatLoading(true);
    try {
      const res = await getAIMessagesByThreadCall(thread.thread_id);
      const mapped = Array.isArray(res)
        ? [...res]
            .reverse()
            .map((m) => ({
              id: m._id,
              sender: m?.user?._id === "ai" ? "ai" : "user",
              text: m?.text || "",
              image: m?.image || m?.file?.url || "",
              createdAt: m?.createdAt,
            }))
        : [];
      setMessages(mapped);
    } finally {
      setChatLoading(false);
    }
  };

  const sendMessage = async () => {
    const question = input.trim();
    if (!question || !selectedThread?.thread_id || sending) return;
    setSending(true);
    setInput("");
    setMessages((prev) => [
      ...prev,
      { id: `tmp-user-${Date.now()}`, sender: "user", text: question, image: "" },
    ]);
    try {
      const formData = new FormData();
      formData.append("body", question);
      formData.append("type", "ai");
      formData.append("entity_type", "aiQuestion");
      formData.append("thread_id", selectedThread.thread_id);
      formData.append("title", "histology");
      const res = await askAIChatCall(formData);
      const aiMessage = res?.messages?.[1]?.content || "";
      if (aiMessage) {
        setMessages((prev) => [
          ...prev,
          { id: `tmp-ai-${Date.now()}`, sender: "ai", text: aiMessage, image: "" },
        ]);
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={2} pb={2}>
        <Card sx={{ borderRadius: 3, p: 2 }}>
          <Stack
            direction={isMobile ? "column" : "row"}
            justifyContent="space-between"
            alignItems={isMobile ? "stretch" : "center"}
            spacing={1.5}
            mb={2}
          >
            <Box>
              <Typography variant="h5" fontWeight={700}>
                AI History
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Continue previous chats and review image-based questions.
              </Typography>
            </Box>
            <TextField
              size="small"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search conversations"
              sx={{ width: isMobile ? "100%" : 320 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Stack>

          {loading ? (
            <Box sx={{ py: 6, textAlign: "center" }}>
              <CircularProgress size={28} />
            </Box>
          ) : filteredThreads.length === 0 ? (
            <Paper variant="outlined" sx={{ p: 3, textAlign: "center", borderRadius: 2 }}>
              <Typography variant="body2" color="text.secondary">
                No AI conversations found.
              </Typography>
            </Paper>
          ) : (
            <Stack spacing={1.2}>
              {pagedThreads.map((thread) => {
                const preview = thread.first_user_message || "No preview";
                const image = thread.picture_url || thread.uploaded_file?.url || "";
                return (
                  <Paper
                    key={thread.thread_id}
                    variant="outlined"
                    onClick={() => openThread(thread)}
                    sx={{
                      p: 1.4,
                      borderRadius: 2,
                      cursor: "pointer",
                      transition: "all .2s ease",
                      "&:hover": {
                        transform: "translateY(-1px)",
                        boxShadow: "0 8px 22px rgba(2, 6, 23, 0.08)",
                        borderColor: "info.main",
                      },
                    }}
                  >
                    <Stack direction="row" spacing={1.2}>
                      {image ? (
                        <Box
                          component="img"
                          src={image}
                          alt="question"
                          sx={{
                            width: 64,
                            height: 64,
                            borderRadius: 1.5,
                            objectFit: "cover",
                            border: "1px solid",
                            borderColor: "rgba(148,163,184,.35)",
                          }}
                        />
                      ) : (
                        <Avatar
                          variant="rounded"
                          sx={{ width: 64, height: 64, bgcolor: "rgba(59,130,246,.12)", color: "#1d4ed8" }}
                        >
                          <ChatRoundedIcon />
                        </Avatar>
                      )}

                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                          <Chip size="small" label={thread.groupLabel || "Recent"} />
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <AccessTimeIcon sx={{ fontSize: 14, color: "text.secondary" }} />
                            <Typography variant="caption" color="text.secondary">
                              {new Date(thread.created_at).toLocaleString()}
                            </Typography>
                          </Stack>
                        </Stack>
                        <Typography
                          variant="body2"
                          sx={{
                            mt: 0.7,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                          }}
                        >
                          {preview}
                        </Typography>
                        {image ? (
                          <Stack direction="row" spacing={0.4} alignItems="center" mt={0.6}>
                            <ImageOutlinedIcon sx={{ fontSize: 14, color: "text.secondary" }} />
                            <Typography variant="caption" color="text.secondary">
                              Contains image
                            </Typography>
                          </Stack>
                        ) : null}
                      </Box>
                    </Stack>
                  </Paper>
                );
              })}
              <Stack
                direction={isMobile ? "column" : "row"}
                alignItems="center"
                justifyContent="space-between"
                spacing={1}
                pt={0.5}
              >
                <Typography variant="caption" color="text.secondary">
                  Showing {filteredThreads.length === 0 ? 0 : (page - 1) * pageSize + 1}
                  -{Math.min(page * pageSize, filteredThreads.length)} of {filteredThreads.length}
                </Typography>
                <Pagination
                  page={page}
                  count={pageCount}
                  color="primary"
                  shape="rounded"
                  onChange={(_, value) => setPage(value)}
                  size={isMobile ? "small" : "medium"}
                />
              </Stack>
            </Stack>
          )}
        </Card>
      </MDBox>

      <Dialog
        open={chatOpen}
        onClose={() => setChatOpen(false)}
        fullScreen={isMobile}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : 3,
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle sx={{ pr: 6 }}>
          Continue AI Chat
          <IconButton
            onClick={() => setChatOpen(false)}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {chatLoading ? (
            <Box sx={{ py: 4, textAlign: "center" }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <>
              <Paper
                variant="outlined"
                sx={{
                  p: 1.25,
                  maxHeight: isMobile ? "62vh" : 460,
                  overflowY: "auto",
                  backgroundColor: "#f8fafc",
                  borderRadius: 2,
                }}
              >
                {messages.map((msg) => (
                  <Box
                    key={msg.id}
                    sx={{
                      display: "flex",
                      justifyContent: msg.sender === "user" ? "flex-end" : "flex-start",
                      mb: 1.2,
                    }}
                  >
                    <Box
                      sx={{
                        maxWidth: "88%",
                        borderRadius: 1.5,
                        padding: msg.sender === "ai" ? "8px" : "10px",
                        bgcolor: msg.sender === "user" ? "#1976d2" : "#e9ecef",
                        color: msg.sender === "user" ? "#fff" : "#111",
                        boxShadow:
                          msg.sender === "user"
                            ? "0 4px 10px rgba(25,118,210,0.22)"
                            : "0 2px 10px rgba(15,23,42,0.08)",
                      }}
                    >
                      {msg.image ? (
                        <Box
                          component="img"
                          src={msg.image}
                          alt="chat attachment"
                          sx={{
                            width: "100%",
                            maxWidth: 280,
                            borderRadius: 1,
                            mb: msg.text ? 1 : 0,
                            objectFit: "cover",
                          }}
                        />
                      ) : null}
                      {msg.sender === "ai" ? (
                        renderAiHtml(msg.text || "")
                      ) : (
                        <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                          {msg.text}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                ))}
              </Paper>

              <Stack direction="row" spacing={1} mt={1.2}>
                <TextField
                  fullWidth
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Type a follow-up question..."
                  multiline
                  minRows={1}
                  maxRows={4}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      sendMessage();
                    }
                  }}
                />
                <MDButton
                  color="info"
                  variant="contained"
                  disabled={sending || !input.trim()}
                  onClick={sendMessage}
                >
                  {sending ? <CircularProgress size={18} color="inherit" /> : <SendIcon fontSize="small" />}
                </MDButton>
              </Stack>
            </>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}

export default AIHistory;
